import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  GetReportsListByOrgQueryParams,
  GetReportsListByUserQueryParams,
  GetReportsListResponse,
} from '@appTypes/webscript';
import { getArnicaRootUrl } from '@config';
import {
  basicReportsError,
  basicReportsResponse,
} from '@modules/reports/basicReports';
import {
  type NormalizedReportItem,
  processResponse,
} from '@modules/reports/basicReports/duck/utils';

import { tagTypes } from './constants';

export const webscriptApi = createApi({
  reducerPath: 'webscript',
  tagTypes: Object.keys(tagTypes),
  baseQuery: fetchBaseQuery({
    baseUrl: getArnicaRootUrl(),
    credentials: 'include',
    prepareHeaders: (headers, api) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const jwt = (api.getState() as any).user?.loginData?.token;
      if (jwt) {
        headers.set('Authorization', `Bearer ${jwt}`);
      }
      return headers;
    },
    validateStatus: (response, body) => {
      const httpStatusOK = response.status >= 200 && response.status <= 299;
      // This is usually returned ad `'1'` by Arnica APIs
      const apiResponseCode = body.ResponseCode || body.response_code;
      const codeOK =
        apiResponseCode && (apiResponseCode === '1' || +apiResponseCode === 1);
      return httpStatusOK && codeOK;
    },
  }),
  endpoints: builder => ({
    GetReportListByOrg: builder.query<
      {
        reports: NormalizedReportItem[];
        organizationGuid: string;
      },
      { params: GetReportsListByOrgQueryParams }
    >({
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.ReportsList,
            id: arg.params.OrganizationGUID,
          },
        ];
      },
      query: arg => ({
        url: '',
        params: {
          Method: 'ExecuteContainerAPI',
          Container: 'SBLReportMenu',
          API: 'GetReportListByOrg',
          ...arg.params,
        },
      }),
      transformResponse: (result: GetReportsListResponse, _meta, arg) => ({
        reports: processResponse(result),
        organizationGuid: arg.params.OrganizationGUID,
      }),
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(basicReportsResponse(data));
        } catch (err) {
          dispatch(
            basicReportsError({
              error: err,
              organizationGuid: params.OrganizationGUID,
            }),
          );
        }
      },
    }),

    GetReportListByUser: builder.query<
      {
        reports: NormalizedReportItem[];
        personGuid: string;
      },
      { params: GetReportsListByUserQueryParams }
    >({
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            type: tagTypes.ReportsList,
            id: arg.params.PersonGUID,
          },
        ];
      },
      query: arg => ({
        url: '',
        params: {
          Method: 'ExecuteContainerAPI',
          Container: 'SBLReportMenu',
          API: 'GetReportListByUser',
          ...arg.params,
        },
      }),
      transformResponse: (result: GetReportsListResponse, _meta, arg) => ({
        reports: processResponse(result),
        personGuid: arg.params.PersonGUID,
      }),
      async onQueryStarted({ params }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(basicReportsResponse(data));
        } catch (err) {
          dispatch(
            basicReportsError({
              error: err,
              organizationGuid: params.PersonGUID,
            }),
          );
        }
      },
    }),
  }),
});

export const webscriptEndpoints = webscriptApi.endpoints;

export const {
  useGetReportListByOrgQuery,
  useLazyGetReportListByOrgQuery,
  useGetReportListByUserQuery,
} = webscriptApi;
